import type { ActivityType } from './activity'

/** Catégories de préférences (regroupent plusieurs types d'activité). */
export const NOTIFICATION_CATEGORIES = ['moves', 'adds', 'deletes', 'loans', 'members'] as const
export type NotificationCategory = (typeof NOTIFICATION_CATEGORIES)[number]

export const NOTIFICATION_CHANNELS = ['in_app', 'email', 'push'] as const
export type NotificationChannel = (typeof NOTIFICATION_CHANNELS)[number]

/** `all` = tout ; `important` = déplacements / suppressions / prêts / membres ; `daily` = résumé ; `off` = rien. */
export const NOTIFICATION_FREQUENCIES = ['all', 'important', 'daily', 'off'] as const
export type NotificationFrequency = (typeof NOTIFICATION_FREQUENCIES)[number]

/** Presets UI (mode simplifié). */
export const NOTIFICATION_PRESETS = ['everything', 'important', 'daily', 'nothing'] as const
export type NotificationPreset = (typeof NOTIFICATION_PRESETS)[number]

export const IMPORTANT_NOTIFICATION_CATEGORIES: ReadonlySet<NotificationCategory> = new Set([
  'moves',
  'deletes',
  'loans',
  'members',
])

const ACTIVITY_TO_CATEGORY: Partial<Record<ActivityType, NotificationCategory>> = {
  'item.moved': 'moves',
  'location.moved': 'moves',
  'item.created': 'adds',
  'item.updated': 'adds',
  'item.restored': 'adds',
  'location.created': 'adds',
  'location.updated': 'adds',
  'location.restored': 'adds',
  'location.batch_created': 'adds',
  'photo.created': 'adds',
  'attachment.created': 'adds',
  'item.deleted': 'deletes',
  'location.deleted': 'deletes',
  'photo.deleted': 'deletes',
  'attachment.deleted': 'deletes',
  'loan.lent': 'loans',
  'loan.returned': 'loans',
  'member.joined': 'members',
  'member.left': 'members',
  'member.removed': 'members',
}

export function activityCategory(type: ActivityType): NotificationCategory | null {
  return ACTIVITY_TO_CATEGORY[type] ?? null
}

/**
 * Fréquence par défaut d'un canal (brief) : in-app tout ; e-mail off sauf résumé des ajouts ; push pour ce qui
 * compte (déplacements, suppressions, prêts, membres), pas pour les ajouts.
 * Pour le push, `inApp` (préférence in-app enregistrée) évite de réveiller quelqu'un qui avait choisi « résumé »
 * ou « rien » avant l'arrivée du push : son push reste coupé.
 */
export function defaultNotificationFrequency(
  category: NotificationCategory,
  channel: NotificationChannel,
  inApp?: NotificationFrequency,
): NotificationFrequency {
  if (channel === 'in_app') return 'all'
  if (channel === 'push') {
    if (inApp === 'off' || inApp === 'daily') return 'off'
    return IMPORTANT_NOTIFICATION_CATEGORIES.has(category) ? 'all' : 'off'
  }
  if (category === 'adds') return 'daily'
  return 'off'
}

export function shouldNotifyNow(frequency: NotificationFrequency, category: NotificationCategory): boolean {
  if (frequency === 'off' || frequency === 'daily') return false
  if (frequency === 'all') return true
  return IMPORTANT_NOTIFICATION_CATEGORIES.has(category)
}

export function shouldIncludeInDailyDigest(
  frequency: NotificationFrequency,
  category: NotificationCategory,
): boolean {
  if (frequency === 'off') return false
  if (frequency === 'daily') return true
  if (frequency === 'all') return true
  return IMPORTANT_NOTIFICATION_CATEGORIES.has(category)
}
